import type { ReportDocument } from '../report-document';

/**
 * WHAT A GENERATED REPORT IS — M10 / S10-04.
 *
 * **THIS MODULE EXISTS TO KEEP jsPDF OUT OF THE STARTUP BUNDLE, AND A TEST
 * CAUGHT THAT IT WAS NOT.** The preview screen needs three small things at all
 * times — the artifact's shape, the staleness fingerprint, and the branding
 * failure — while the generator itself should load only when Brandon asks for a
 * report. Importing them from `generate-report-pdf.ts` dragged the whole PDF
 * engine and both font files into the first bytes of the application.
 *
 * **NOTHING HERE IMPORTS jsPDF, AND NOTHING HERE MAY.**
 *
 * **IT IS ALSO THE S10-05 SEAM.** `ReportPdfArtifact` is deliberately the shape
 * a saved report would persist: the bytes, how many pages, when it was made,
 * what it is called, and which content produced it. S10-04 keeps it in memory
 * for one screen; S10-05 may store it, with the `ReportDocument` snapshot beside
 * it, without re-deriving anything.
 */

export interface ReportPdfArtifact {
  readonly bytes: Uint8Array;
  readonly pageCount: number;
  readonly generatedAt: string;
  readonly filename: string;
  /**
   * Which builder content produced these bytes.
   *
   * **THE POINT IS STALENESS.** If Brandon steps back, changes a photo and
   * returns, the artifact he might View or Share is no longer his report.
   */
  readonly fingerprint: string;
}

export class ReportBrandingUnavailable extends Error {
  constructor() {
    super('report-branding-unavailable');
    this.name = 'ReportBrandingUnavailable';
  }
}

/**
 * A stable description of everything that can change what the report contains.
 *
 * Deliberately built from the CONTENT MODEL rather than from the builder's
 * internals: anything that changes what a client would receive changes this
 * string, and anything that does not, does not.
 *
 * **IT IS NOT TRUNCATED, AND THAT IS THE WHOLE POINT (M-1, final review,
 * 2026-08-19).** This used to end `.slice(0, 4096)`. On a large report that cap
 * fell inside the Capture list, so **every change past it was invisible**:
 * Brandon could go back, swap a photo in a late Capture, return, and the
 * artifact would still look current — and View and Share would hand his client
 * a document built from the evidence he had just replaced. A staleness check
 * that stops reading partway through is worse than none, because it is trusted.
 *
 * **NOTHING REQUIRES IT TO BE SHORT.** The string lives in memory for the life
 * of one screen. It is never persisted, transmitted, rendered, logged or used
 * as an identifier — it is only ever compared with the previous one, so its
 * length costs a string comparison and nothing else.
 */
export function reportContentFingerprint(document: ReportDocument): string {
  const branding = document.branding
    ? [
        document.branding.companyName,
        document.branding.email ?? '',
        document.branding.phone ?? '',
        document.branding.logo ? document.branding.logo.base64.length.toString(10) : 'none',
      ].join('|')
    : 'off';

  const captures = document.captures
    .map((capture) =>
      [
        capture.captureId,
        capture.title,
        capture.meta,
        capture.before.map((photo) => photo.key).join(','),
        capture.after.map((photo) => photo.key).join(','),
      ].join('~'),
    )
    .join(';');

  return [
    branding,
    document.projectName,
    document.reportDate,
    document.description ?? '',
    captures,
  ].join('#');
}
